import { useState } from 'react';

import { Loader2 } from 'lucide-react';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

import { AccountCheckboxList } from './AccountCheckboxList';

import type { TemplateGroup } from './group-templates';

interface DeleteTemplateDialogProps {
  group: TemplateGroup | null;
  isPending: boolean;
  onConfirm: (group: TemplateGroup, wabaIds: string[]) => void;
  onClose: () => void;
}

/** Confirms deleting a template group from the selected WhatsApp Business Accounts. */
export const DeleteTemplateDialog = ({
  group,
  isPending,
  onConfirm,
  onClose,
}: DeleteTemplateDialogProps) => {
  // Keyed by group so the dialog re-mounts with everything checked for each new group.
  return group ? (
    <DeleteTemplateDialogBody
      key={group.key}
      group={group}
      isPending={isPending}
      onConfirm={onConfirm}
      onClose={onClose}
    />
  ) : null;
};

const DeleteTemplateDialogBody = ({
  group,
  isPending,
  onConfirm,
  onClose,
}: DeleteTemplateDialogProps & { group: TemplateGroup }) => {
  const existing = group.accounts.filter((a) => a.row);
  const [selected, setSelected] = useState<string[]>(existing.map((a) => a.waba_id));

  const toggle = (wabaId: string, checked: boolean) =>
    setSelected((prev) => (checked ? [...prev, wabaId] : prev.filter((id) => id !== wabaId)));

  return (
    <AlertDialog open onOpenChange={(open) => !open && !isPending && onClose()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            Delete “{group.name}” ({group.language})?
          </AlertDialogTitle>
          <AlertDialogDescription>
            This removes the template from the selected WhatsApp Business Accounts on Meta.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AccountCheckboxList
          accounts={existing}
          selected={selected}
          onToggle={toggle}
          disabled={isPending}
        />
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isPending}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            disabled={selected.length === 0 || isPending}
            onClick={(e) => {
              e.preventDefault();
              onConfirm(group, selected);
            }}
          >
            {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Delete
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
